import {
  createContext,
  type Dispatch,
  type KeyboardEvent,
  type PropsWithChildren,
  type SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

type TitleContextProps = {
  /**
   * The main heading of the list item. Used for the aria-label of the list item and displayed visually.
   */
  title: string
  titleAction: ((e: KeyboardEvent<HTMLLIElement>) => void) | null
  setTitleAction: Dispatch<SetStateAction<((e: KeyboardEvent<HTMLLIElement>) => void) | null>>
  headingRef: React.MutableRefObject<HTMLDivElement | null>
}

const TitleContext = createContext<TitleContextProps | undefined>(undefined)

export const TitleProvider = ({children}: PropsWithChildren) => {
  const [title, setTitle] = useState('')
  const [titleAction, setTitleAction] = useState<TitleContextProps['titleAction']>(null)
  const headingRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (headingRef?.current?.textContent) {
      setTitle(headingRef.current.textContent.trim())
    }
  }, [headingRef, setTitle])

  const contextProps = useMemo(
    () =>
      ({
        title,
        titleAction,
        setTitleAction,
        headingRef,
      }) satisfies TitleContextProps,
    [title, titleAction],
  )
  return <TitleContext.Provider value={contextProps}>{children}</TitleContext.Provider>
}
TitleProvider.displayName = 'ListItemTitleProvider'

export const useListItemTitle = () => {
  const context = useContext(TitleContext)
  if (!context) throw new Error('useListItemTitle must be used with TitleProvider.')
  return context
}

try{ TitleContext.displayName ||= 'TitleContext' } catch {}